import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  Alert,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Autocomplete,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useSnackbar } from '../../Stateless/SnackBarProvider';
import { GridRowId } from '@mui/x-data-grid';
import CommonDataGrid from '../../Stateless/CommonDataGrid';
import ErrorHandler from '../../Stateless/Common/ErrorHandler';
import {
  useBlockUnblockStoreItemById,
  useDeleteStoreItemById,
  useGetAllStoreItem,
} from '../../Query/StoreItemQuery';
import { useAllStores } from '../../Query/ManageStoreQuery';
import { useSetAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { categoryStyles } from '../../Styles/CategoryFormStyles';
import { useDebounce } from '../Utils/useDounce';
import PermissionActionButtons from '../../Stateless/Common/PermissionActionButtons';
import { usePermissions } from '../../../context/PermissionContext';
import { Add } from '@mui/icons-material';
import { AddOrEditItemRequestSchemaItemAvailableTimeEnum } from '../../../client-sdk/api';
import EditStoreOwnerModal from '../EditStoreOwnerModal';
import AddStoreItem from './AddStoreItem';
import { Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useItemManagementParams } from './useItemManagementParams';
import BulkUploadModal from './BulkUploadModal';
import { BulkUploadItemAddedByEnum } from '../../../client-sdk/api';
import UploadFileIcon from '@mui/icons-material/UploadFile';

interface StoreItem {
  id: string;
  itemName: string;
  itemNameArabic: string;
  storeName: string;
  categoryName?: string;
  subCategoryName?: string;
  itemCategoryId: string;
  itemSubCategoryId: string;
  restaurantId: string;
  originalPrice: number;
  discountedPrice: number;
  itemQuantity: number;
  itemContent: string;
  itemContentArabic: string;
  discount: boolean;
  vegOrNonVeg: 'veg' | 'non-veg';
  tax: boolean;
  taxName: string;
  taxNameArabic: string;
  taxPercentage: number;
  comboPack: boolean;
  isHalal: boolean;
  itemAvailableTime: AddOrEditItemRequestSchemaItemAvailableTimeEnum[];
  description: string;
  descriptionArabic: string;
  metaKeywords: string;
  metaKeyWordsArabic: string;
  metaDescription: string;
  metaDescriptionArabic: string;
  hasChoice: boolean;
  hasSpecification: boolean;
  itemSpecification: {
    specificationTitle: string;
    specificationDescription: string;
  }[];
  itemImage: string[];
  isStockAvailable: string;
  addedBy: 'Admin' | 'Merchant';
  status: 'blocked' | 'unblocked';
}

interface EditFormData extends Omit<StoreItem, 'isStockAvailable'> {
  isStockAvailable: boolean;
}

type StoreItemListProps = {
  editCategory: (category: StoreItem) => void;
  modal?: boolean;
};
export const categoryStoreAtom = atomWithStorage('categoryStore', null);

const StoreItemList: React.FC<StoreItemListProps> = ({ editCategory }) => {
  const { showMessage } = useSnackbar();
  const { hasPermission, userType } = usePermissions();
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [bulkUploadModalOpen, setBulkUploadModalOpen] = useState(false);
  const setCategoryStoreAtom = useSetAtom(categoryStoreAtom);
  const [editMode, setEditMode] = useState<'add' | 'edit'>('add');
  const [selectedItem, setSelectedItem] = useState<EditFormData | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage] = useState<string>('');
  const [snackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>(
    'info'
  );

  const { params, dispatch } = useItemManagementParams();
  // Use API search to search across all pages
  const { data: storeitem, isLoading, refetch } = useGetAllStoreItem(params);

  // Fetch stores for dropdown
  const { data: storesData } = useAllStores({
    page: 1,
    limit: 100,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    statusFilter: 'All',
    addedByFilter: 'All',
  });

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);

  const deleteStoreItemMutation = useDeleteStoreItemById();
  const { mutateAsync: blockUnblockStoreItemAsync } =
    useBlockUnblockStoreItemById();

  const handleOpenAddModal = () => {
    setEditMode('add');
    setSelectedItem(null);
    setAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
    setEditMode('add');
    setSelectedItem(null);
  };

  // Get storeId and merchantId for bulk upload
  const getBulkUploadParams = () => {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const storeId = userData?.userDetails?.storeId || '';
    const merchantId = userData?.userId || undefined;
    const addedBy: BulkUploadItemAddedByEnum = 
      userType === 'admin' || userType === 'subAdmin' 
        ? BulkUploadItemAddedByEnum.Admin 
        : BulkUploadItemAddedByEnum.Merchant;
    
    return { storeId, merchantId, addedBy };
  };

  const handleBulkUploadSuccess = () => {
    refetch();
  };

  const handleEditCategory = (category: StoreItem) => {
    // Convert isStockAvailable from string to boolean for EditFormData
    const editData: EditFormData = {
      ...category,
      isStockAvailable: category.isStockAvailable === 'Yes',
    };
    setSelectedItem(editData);
    setEditMode('edit');
    setAddModalOpen(true);
    editCategory(category); // Call the editCategory prop
  };

  const rows = useMemo(() => {
    if (!storeitem?.data) return [];
    return (storeitem.data || []).map((row: any, index: number) => ({
      ...row,
      id: row.itemId || row.id,
      sno: index + 1 + (Number(params.page - 1)) * Number(params.limit),
      itemName: row.itemName || '',
      storeName: row.storeName || '',
    }));
  }, [storeitem, params.page, params.limit]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  };

  useEffect(() => {
    dispatch({ type: 'SET_SEARCH', payload: debouncedSearch ? debouncedSearch : undefined });
  }, [debouncedSearch, dispatch]);

  const handleStoreChange = (_: any, newValue: { value: string; label: string } | null) => {
    const storeId = newValue?.value === '' || !newValue ? undefined : newValue.value;
    dispatch({ type: 'SET_STORE_ID', payload: storeId });
  };

  // Prepare store options for dropdown
  const storeOptions = useMemo(() => {
    if (!storesData?.data) return [];
    return storesData.data.map((store: any) => ({
      value: store.id,
      label: store.restaurantDetails?.storeName || store.nameEnglish || store.name || 'Unknown Store',
    }));
  }, [storesData]);

  const handlePageChange = (newPage: number) => {
    dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
  };

  const handlePageSizeChange = (newSize: number) => {
    dispatch({ type: 'SET_LIMIT', payload: newSize });
  };

  const handleBlock = async () => {
    if (selectedCategoryIds.length === 0) {
      showMessage('No Store item selected to block', 'error');
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to block ${selectedCategoryIds.length} Store item(s)?`
      )
    ) {
      try {
        await blockUnblockStoreItemAsync({
          id: selectedCategoryIds,
          data: { status: 'blocked' },
        });
        showMessage('Store item blocked successfully', 'success');
      } catch {
        showMessage('Failed to block StoreItem', 'error');
      }
    }
  };

  const handleUnblock = async () => {
    if (selectedCategoryIds.length === 0) {
      showMessage('No Store Item selected to unblock', 'error');
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to unblock ${selectedCategoryIds.length} Store item(s)?`
      )
    ) {
      try {
        await blockUnblockStoreItemAsync({
          id: selectedCategoryIds,
          data: { status: 'unblocked' },
        });
        showMessage('Store Item unblocked successfully', 'success');
      } catch {
        showMessage('Failed to unblock Store Item', 'error');
      }
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this Store Item?')) {
      deleteStoreItemMutation.mutate([id], {
        onSuccess: () => {
        },
      });
    }
  };

  const handleDeleteSelected = () => {
    if (selectedCategoryIds.length === 0) {
      showMessage('No Store Items selected to delete', 'error');
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to delete ${selectedCategoryIds.length} Store Item(s)?`
      )
    ) {
      deleteStoreItemMutation.mutate(selectedCategoryIds, {
        onSuccess: () => {
          setSelectedCategoryIds([])
          showMessage(
            `${selectedCategoryIds.length} Store Item(s) deleted successfully`,
            'success'
          );
        },
        onError: (error: any) => {
          showMessage(error?.message || 'Failed to delete Store Item', 'error');
        },
      });
    }
  };
  // Determine bulk visibility based on selection statuses
  const { showBlock, showUnblock } = useMemo(() => {
    if (!selectedCategoryIds || selectedCategoryIds.length === 0) return { showBlock: false, showUnblock: false };
    const dataList = storeitem?.data || [];
    const idToStatus = new Map<string, string>(
      dataList.map((item: any) => [String(item.itemId || item.id), String(item.status || '')])
    );
    const statuses = selectedCategoryIds.map((id) => idToStatus.get(String(id)) || '');
    const hasBlocked = statuses.some((s) => s === 'blocked');
    const hasUnblocked = statuses.some((s) => s === 'unblocked');
    return { showBlock: hasUnblocked, showUnblock: hasBlocked };
  }, [selectedCategoryIds, storeitem]);

  const RowActions: React.FC<{ row: any }> = ({ row }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const canEdit = hasPermission('item', 'Edit');
    const canDelete = hasPermission('item', 'Delete');
    if (!canEdit && !canDelete) return null;
    const id = typeof row.id === 'string' ? row.id : '';
    return (
      <>
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
          {canEdit && (
            <MenuItem onClick={() => { setAnchorEl(null); handleEditCategory(row); setCategoryStoreAtom(row.id); }}>
              <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
          )}
          {canDelete && (
            <MenuItem onClick={() => { setAnchorEl(null); if (id) handleDelete(id); }}>
              <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </>
    );
  };

  const columns = [
    {
      field: 'sno',
      headerName: 'S.No',
      width: 90,
    },
    {
      field: 'itemName',
      headerName: 'Item Name',
    },
    {
      field: 'storeName',
      headerName: 'Store Name',
      width: 200,
    },
    {
      field: 'originalPrice',
      headerName: 'Original Price',
      width: 200,
    },
    {
      field: 'discountedPrice',
      headerName: 'Discounted Price',
      width: 200,
      renderCell: (params: any) => {
        const value = params.value;
        // Show empty if value is 0, null, or undefined
        if (value === 0 || value === null || value === undefined) {
          return <Typography variant="body2">—</Typography>;
        }
        return <Typography variant="body2">{value}</Typography>;
      },
    },
    {
      field: 'itemImage',
      headerName: 'Item Image',
      flex: 1,
      renderCell: (params: { row: { itemImage: any[] } }) => {
        const imageUrl = params.row.itemImage?.[0]; // Get the first image URL
        return imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt="Item"
            sx={{
              width: 50,
              height: 50,
              objectFit: 'cover',
              borderRadius: 1,
            }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            No image
          </Typography>
        );
      },
    },
    { field: 'isStockAvailable', headerName: 'Stock Availablity', flex: 1 },
    { field: 'addedBy', headerName: 'Added By', width: 100 },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      filterable: false,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: any) => {
        const isBlocked = params.value === 'blocked';
        const id = params.row.id as string;
        const canBlock = hasPermission('item', 'Block');
        const canUnblock = hasPermission('item', 'Unblock');
        if ((isBlocked && !canUnblock) || (!isBlocked && !canBlock)) return null;
        return (
          <IconButton size="small" sx={{ color: isBlocked ? '#f44336' : '#4caf50' }} onClick={() => isBlocked ? blockUnblockStoreItemAsync({ id: [id], data: { status: 'unblocked' } }) : blockUnblockStoreItemAsync({ id: [id], data: { status: 'blocked' } })}>
            {isBlocked ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
          </IconButton>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      filterable: false,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: any) => <RowActions row={params.row} />,
    },
  ];

  const handleCategorySelection = (selectionModel: GridRowId[]) => {
    const ids = selectionModel.map((id) => String(id));
    setSelectedCategoryIds(ids);
  };

  return (
    <>
      <Typography variant="h5" fontWeight="bold" mb={2}>
        Item Management
      </Typography>
      <Box sx={categoryStyles.ListContainer}>
        {snackbarOpen && (
          <Box sx={{ mb: 2 }}>
            <Alert
              severity={snackbarSeverity}
              onClose={() => setSnackbarOpen(false)}
            >
              {snackbarMessage}
            </Alert>
          </Box>
        )}

        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          wrap="wrap"
          sx={{ mb: 2 }}
        >
          <Grid container spacing={2} alignItems="center" sx={{ flex: 1 }}>
            <Grid>
              <Box
                sx={{
                  width: 400,
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                  background: '#fff',
                }}
              >
                <TextField
                  fullWidth
                  size="medium"
                  placeholder="Search by item name or item code"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  sx={{
                    '.MuiOutlinedInput-root': {
                      height: 40,
                      width: 400,
                      border: 'none', // remove inner border
                    },
                    '& fieldset': {
                      border: 'none', // remove Material-UI default border
                    },
                    '& .MuiInputBase-input': {
                      paddingTop: '10px', // adjust this value to move the placeholder lower
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon
                          sx={{ fontSize: 20, color: '#aaa', mt: '4px' }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Grid>
            <Grid>
              <Autocomplete
                options={storeOptions}
                getOptionLabel={(option) => option.label || ''}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                value={storeOptions.find((opt) => opt.value === params.storeId) || null}
                onChange={handleStoreChange}
                size="small"
                sx={{
                  width: 600,
                }}
                filterOptions={(options, { inputValue }) => {
                  // Custom filter for case-insensitive search
                  return options.filter((option) =>
                    option.label.toLowerCase().includes(inputValue.toLowerCase())
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search and select a store"
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '50px',
                        minHeight: '50px',
                        width: '300px',
                      },
                      '& .MuiInputBase-input': {
                        padding: '12px 14px',
                        fontSize: '15px',
                      },
                    }}
                  />
                )}
                noOptionsText="No stores found"
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.value}>
                    {option.label}
                  </Box>
                )}
              />
            </Grid>
          </Grid>
          <Grid
            container
            justifyContent="flex-end"
            spacing={1}
            alignItems="center"
          >
            <Grid>
              <PermissionActionButtons
                module="item"
                onBlock={showBlock ? handleBlock : undefined}
                onUnblock={showUnblock ? handleUnblock : undefined}
                onDelete={selectedCategoryIds.length ? handleDeleteSelected : undefined}
              />
            </Grid>
            <Grid>
              {hasPermission('item', 'Add') && (
                <>
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={() => setBulkUploadModalOpen(true)}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 2,
                      boxShadow: 'none',
                      mr: 1,
                      '&:hover': {
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      },
                    }}
                    startIcon={<UploadFileIcon sx={{ fontSize: 16 }} />}
                  >
                    Bulk Upload
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleOpenAddModal}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 2,
                      boxShadow: 'none',
                      '&:hover': {
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      },
                    }}
                    startIcon={<Add sx={{ fontSize: 16}} />}
                  >
                    Add Item
                  </Button>
                </>
              )}
            </Grid>
          </Grid>
        </Grid>

        <CommonDataGrid
          key={`datagrid-page-${params.page}-limit-${params.limit}`}
          rows={rows || []}
          columns={columns}
          pageSize={params.limit}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          checkboxSelection
          loading={isLoading}
          page={params.page - 1}
          rowCount={storeitem?.pagination?.totalItems}
          components={{
            NoRowsOverlay: () => <ErrorHandler type="no-data" message="No categories found" onRetry={() => refetch()} />
          }}
          onRowSelectionModelChange={handleCategorySelection}
          selectionModel={selectedCategoryIds}
        />

        <EditStoreOwnerModal
          open={addModalOpen}
          onClose={handleCloseAddModal}
          title={editMode === 'add' ? 'Add Item' : 'Edit Item'}
        >
          <AddStoreItem
            onClose={handleCloseAddModal}
            mode={editMode}
            initialData={selectedItem}
          />
        </EditStoreOwnerModal>

        {(() => {
          const { storeId, merchantId, addedBy } = getBulkUploadParams();
          return (
            <BulkUploadModal
              open={bulkUploadModalOpen}
              onClose={() => {
                setBulkUploadModalOpen(false);
                if (!storeId) {
                  showMessage('Please select a store first', 'error');
                }
              }}
              storeId={storeId}
              merchantId={merchantId}
              addedBy={addedBy}
              onSuccess={handleBulkUploadSuccess}
            />
          );
        })()}
      </Box>
    </>
  );
};

export default StoreItemList;
